import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '../auth/AuthProvider'
import {
  hasContinuedWelcome,
  rememberWelcomeContinued,
  reportWelcomeStep,
  shouldShowDriverInviteWelcome,
} from '../lib/driverInviteWelcome'
import { lazyWithReload } from '../lib/lazyWithReload'

const DriverInviteWelcome = lazyWithReload(() => import('../pages/DriverInviteWelcome'))

/**
 * Puts the welcome page in front of the driver invite (see lib/driverInviteWelcome). «Продолжить»
 * renders `children` — the invite page exactly as it was — on the same URL, so the invite code,
 * the login return address and the «Открыть в приложении» hand-off are all untouched.
 */
export default function DriverInviteWelcomeGate({ children }: { children: ReactNode }) {
  const { authenticated, initializing } = useAuth()
  const { search } = useLocation()
  const [continued, setContinued] = useState(hasContinuedWelcome)
  const showWelcome = shouldShowDriverInviteWelcome({ search, authenticated, initializing, continued })

  // Once per page view; the server keeps only the first one per invite anyway.
  const reportedShown = useRef(false)
  useEffect(() => {
    if (!showWelcome || reportedShown.current) return
    reportedShown.current = true
    reportWelcomeStep('welcome_shown', search)
  }, [showWelcome, search])

  if (!showWelcome) {
    return <>{children}</>
  }

  const onContinue = () => {
    reportWelcomeStep('welcome_continue', search)
    rememberWelcomeContinued()
    setContinued(true)
    window.scrollTo(0, 0)
  }

  return <DriverInviteWelcome onContinue={onContinue} />
}
